import { createShopifyGuestCustomer } from 'api/auth';
import { useAuth } from 'contexts/auth';
import { useCart } from 'contexts/cart';
import { Address } from 'interfaces/address';
import { isSubscriptionItem } from 'interfaces/line-item';
import { LoginResponse, isAuthError } from 'models/auth';
import { useRouter } from 'next/router';
import React, { useContext } from 'react';
import { useSessionStorage } from 'react-use';
import { useTrackingStore } from 'stores/tracking';
import { Gtm } from 'tracking';
import { CHECKOUT_STEPS, CHECKOUT_STEPS_MOBILE } from './constants';

interface AuthParams {
  email: string;
  password: string;
}
export interface CheckoutContextType {
  step: CHECKOUT_STEPS;

  isCheckoutInitiated: boolean;
  isAuthenticatedForCheckout: boolean;
  register: (params: AuthParams) => void;
  login: (params: AuthParams) => void;
  guestCustomer: (params: { email: string }) => void;

  isExitModalOpen: boolean;
  setExitModal: (modalVisible: boolean) => void;

  isCartExpanded: boolean;
  setCartExpanded: (expanded: boolean) => void;

  newsletterOptin: boolean;
  setNewsletterOptin: (signup: boolean) => void;

  billingAddress: Address | null;
  setBillingAddress: (address: Address | null) => void;
  usingBillingAddress: boolean;
  shouldUseBillingAddress: (useBilling: boolean) => void;

  goToStep: (
    step: CHECKOUT_STEPS | CHECKOUT_STEPS_MOBILE,
    queryString?: { [key: string]: string | number | null | undefined }
  ) => void;

  shouldRegister: boolean;
}

const CheckoutContext = React.createContext<CheckoutContextType>({} as CheckoutContextType);

const getStepFromPath = (asPath: string): CHECKOUT_STEPS | undefined => {
  const lastSegment = asPath.split(/[?#]/)[0].split('/').pop();
  return Object.values(CHECKOUT_STEPS).find((step) => step === lastSegment);
};

export const CheckoutProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const router = useRouter();
  // Next drops route params on a first load under a locale prefix such as /en
  const step = router.query.step ?? getStepFromPath(router.asPath);
  const {
    loading: authLoading,
    isUserLoggedIn,
    billingAddress,
    isGuest,
    usingBillingAddress,
    onLogin,
    onSignUp,
    setBillingAddress,
    setCustomer,
    shouldUseBillingAddress,
  } = useAuth();
  const { mapCheckoutItems } = useTrackingStore();
  const [isExitModalOpen, setExitModal] = React.useState(false);
  const { items } = useCart();

  const [newsletterOptin, setNewsletterOptin] = useSessionStorage<boolean>(
    'pd:checkout-newsletter',
    false
  );

  // on mobile cart is hidden until expanded by user
  const [isCartExpanded, setCartExpanded] = React.useState(false);

  // Prevent browser to scroll while mobile cart is open.
  React.useEffect(() => {
    if (isCartExpanded) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => document.body.classList.remove('overflow-hidden');
  }, [isCartExpanded]);

  /**
   * We can only go through checkout if the user is authenticated or he has entered
   * email for guest checkout, this memoized value checks both.
   * Guests with subscription items must register before proceeding.
   * @returns boolean
   */
  const isAuthenticatedForCheckout = React.useMemo(() => {
    if (authLoading) return false;

    // Fully logged in users (not guests) can always proceed
    if (isUserLoggedIn) return true;

    // Guests can only proceed if they don't have subscription items
    if (isGuest) return !items.some((item) => isSubscriptionItem(item));

    return false;
  }, [authLoading, isUserLoggedIn, isGuest, items]);

  /**
   * Check if user has provided enough information for subscription.
   */
  const shouldRegister = React.useMemo(() => {
    const containsSub = items.some((item) => isSubscriptionItem(item));
    return isGuest && containsSub;
  }, [isGuest, items]);

  React.useEffect(() => {
    if (window) {
      window.scrollTo({ top: 0 });
    }
  }, [isAuthenticatedForCheckout]);

  /**
   * Checks if the checkout is hydrated and ready to use
   * @returns boolean
   */
  const isCheckoutInitiated = React.useMemo(() => {
    return !authLoading;
  }, [authLoading]);

  /**
   * Sign up user during checkout
   * @returns Promise
   */
  const register = React.useCallback(
    async (params: AuthParams) => {
      Gtm.track({
        group: Gtm.Group.CheckoutStandard,
        name: Gtm.CheckoutEvent.SubmitUserRole,
        data: {
          type: 'signup',
        },
      });

      const signUpRes = await onSignUp(params);
      Gtm.session({
        customerEmail: signUpRes.email,
        customerId: signUpRes.customerId,
        customerOrdersCount: 0,
      });
      Gtm.track({
        group: Gtm.Group.CheckoutStandard,
        name: Gtm.CheckoutEvent.UserIdentified,
        data: {
          type: 'signup',
          checkout: {
            items: mapCheckoutItems(items),
          },
        },
        nonInteractive: true,
      });

      return signUpRes;
    },
    [onSignUp, items, mapCheckoutItems]
  );

  /**
   * Login a user during checkout
   * @returns Promise
   */
  const login = React.useCallback(
    async (params: AuthParams) => {
      Gtm.track({
        group: Gtm.Group.CheckoutStandard,
        name: Gtm.CheckoutEvent.SubmitUserRole,
        data: {
          type: 'login',
        },
      });

      let customer: LoginResponse['data']['customer'];
      try {
        customer = await onLogin(params);
      } catch (error) {
        if (!isAuthError(error)) {
          console.error(error, 'unknown error on login');
        }
        throw error;
      }

      Gtm.session({
        customerEmail: customer.email,
        customerId: customer.id,
        customerOrdersCount: customer.ordersCount,
      });
      Gtm.track({
        group: Gtm.Group.CheckoutStandard,
        name: Gtm.CheckoutEvent.UserIdentified,
        data: {
          type: 'login',
          checkout: {
            items: mapCheckoutItems(items),
          },
        },
        nonInteractive: true,
      });
    },
    [items, mapCheckoutItems, onLogin]
  );

  const guestCustomer = React.useCallback(
    async (params: { email: string }) => {
      Gtm.track({
        group: Gtm.Group.CheckoutStandard,
        name: Gtm.CheckoutEvent.SubmitUserRole,
        data: {
          type: 'guest',
        },
      });

      let customer: {
        id: number;
        email: string;
        ordersCount?: number;
      };

      try {
        const res = await createShopifyGuestCustomer({
          email: params.email,
          checkoutAsGuest: true,
        });

        if (res.error) {
          throw res.error;
        }

        customer = res.data.customer;
        setCustomer({
          email: customer.email,
          customerId: customer.id,
          ordersCount: customer.ordersCount ?? 0,
          id: customer.id,
        });
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
    [setCustomer]
  );

  const goToStep = React.useCallback(
    (
      step: CHECKOUT_STEPS | CHECKOUT_STEPS_MOBILE,
      queryString?: { [key: string]: string | number | null | undefined }
    ) => {
      router.push(
        `/checkout/${step}${
          queryString
            ? `?${new URLSearchParams(
                // Sucks, but I don't understand why an object is not compatible with URLSearchParams
                queryString as unknown as URLSearchParams
              ).toString()}`
            : ''
        }`
      );
    },
    [router]
  );

  return (
    <CheckoutContext.Provider
      value={{
        step: step as CHECKOUT_STEPS,

        isCheckoutInitiated,
        isAuthenticatedForCheckout,
        register,
        login,
        guestCustomer,

        isExitModalOpen,
        setExitModal,

        isCartExpanded,
        setCartExpanded,

        newsletterOptin,
        setNewsletterOptin,

        usingBillingAddress,
        shouldUseBillingAddress,
        billingAddress,
        setBillingAddress,

        goToStep,
        shouldRegister,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckout = (): CheckoutContextType => {
  const context = useContext(CheckoutContext);
  if (context === undefined) {
    throw new Error('useCheckout must be used within a CheckoutProvider');
  }
  return context;
};
